import { CommonModule } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Input,
  output,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { MomentModule } from 'ngx-moment';

import type { DisqualifyReason } from '@recruitee/disqualify-reasons-types';
import { TranslatePipe } from '@recruitee/i18n';
import { HiddenResultPreview, InterviewResult } from '@recruitee/interview-results-types';
import type { Placement } from '@recruitee/placements-types';
import type { RequisitionOpeningIssue } from '@recruitee/requisitions-types';
import type { Stage } from '@recruitee/stages-types';
import { StageGroup } from '@recruitee/stages-types';
import {
  CollapsibleCardComponent,
  ConnectedOverlayDirective,
  DateComponent,
  DividerComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OfferStatusIndicatorComponent,
  OverlayOriginDirective,
  OverlayPosition,
  OverlayPositionPairs,
  RtTooltip,
  SpinnerComponent,
  TruncateComponent,
} from '@recruitee/user-interface';
import { WalkthroughStepOriginDirective } from '@recruitee/walkthrough';

import { CandidateWorkLocationsButtonComponent } from '../candidate-preferred-locations-button/candidate-work-locations-button.component';
import type {
  DisqualifyEvent,
  PlacementActions,
  PlacementEvent,
  ProceedEvent,
  ShowAutomatedActionDetailsEvent,
} from '../candidate-profile-placements/types';
import { CollapsibleCardAnalyticsDirective } from '../collapsible-card/collapsible-card-analytics.directive';
import { OverdueCandidateBadgeComponent } from '../overdue-candidate-badge/overdue-candidate-badge.component';
import { ActionButtonsComponent } from './action-buttons/action-buttons.component';
import { PlacementEvaluationMeterComponent } from './evaluation-meter/placement-evaluation-meter.component';
import { PlacementRowComponent } from './placement-row/placement-row.component';
import { StageSelectComponent } from './stage-select/stage-select.component';

@Component({
  selector: 'rt-candidate-profile-placement',
  templateUrl: './candidate-profile-placement.component.html',
  styleUrls: ['./candidate-profile-placement.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CollapsibleCardComponent,
    RtTooltip,
    OfferStatusIndicatorComponent,
    HeadingComponent,
    TruncateComponent,
    IconButtonComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    PlacementRowComponent,
    IconComponent,
    DividerComponent,
    MomentModule,
    DateComponent,
    TranslatePipe,
    ActionButtonsComponent,
    StageSelectComponent,
    SpinnerComponent,
    OverdueCandidateBadgeComponent,
    CandidateWorkLocationsButtonComponent,
    WalkthroughStepOriginDirective,
    CollapsibleCardAnalyticsDirective,
    RouterLink,
    PlacementEvaluationMeterComponent,
  ],
})
export class CandidateProfilePlacementComponent {
  public readonly StageGroup = StageGroup;
  public readonly dropdownPosition = [
    OverlayPositionPairs[OverlayPosition.BottomRight],
    OverlayPositionPairs[OverlayPosition.TopRight],
  ];

  public placement = input.required<Placement>();

  public placementActions = input.required<PlacementActions>();

  public stages = input.required<Stage[]>();

  public disqualifyReasons = input.required<DisqualifyReason[]>();

  public isLoading = input<boolean>(false);

  public openingIssue = input<RequisitionOpeningIssue>();
  public showJobFitScore = input<boolean>(true);

  public interviewResults = input.required<Record<number, InterviewResult[]>>();

  public hiddenResultPreview = input.required<HiddenResultPreview | undefined>();

  public uniqKey = computed(
    () =>
      `candidate-profile-placement:candidate:${this.placement().candidateId}:job:${
        this.placement().offer?.id
      }:placement:${this.placement().id}`,
  );

  public canOpenMoreDropdown = computed(
    () => this.moreDropdownTemplate && this.placementActions().openMoreDropdown,
  );

  public disqualifiedTooltipTemplate = input.required<TemplateRef<null>>();

  @Input()
  public offerTitleTooltipTemplate: TemplateRef<null> | null = null;

  @Input()
  public moreDropdownTemplate: TemplateRef<{ connectedOverlay: ConnectedOverlayDirective }> | null =
    null;

  @Input({ transform: booleanAttribute })
  public collapsible = false;

  @Input({ transform: booleanAttribute })
  public isFirst = true;

  @Input({ transform: booleanAttribute })
  public isLast = true;

  @Input({ transform: booleanAttribute })
  public hasDragHandle = false;

  /**
   * Emits the placement id.
   */
  public editHiringDetailsClick = output<PlacementEvent>();

  public proceedClick = output<ProceedEvent>();

  public disqualifyReasonSelect = output<DisqualifyEvent>();

  public requalifyClick = output<PlacementEvent>();

  public showAutomatedActionDetailsClick = output<ShowAutomatedActionDetailsEvent>();

  public editDisqualifyReasonsClick = output<void>();

  public editWorkLocation = output<PlacementEvent>();

  public disqualifyReasonsActionsDetails = output<DisqualifyReason>();

  public evaluationMeterClick = output<Placement['id']>();

  protected offerUrl = computed(() => `/offers/${this.placement().offer?.slug}/pipeline`);

  protected locationIds = computed(() =>
    this.placement().workLocationId
      ? [this.placement().workLocationId!]
      : this.placement().locationIds || [],
  );

  @ViewChild('moreDropdown')
  private moreDropdown: ConnectedOverlayDirective;

  public onMoreClick($event: MouseEvent): void {
    $event.stopImmediatePropagation();
    this.moreDropdown.toggleOverlay(!this.moreDropdown.open);
  }
}
